// ── 출퇴근 시간관리 훅 ────────────────────────────────────────────────────────
// 시간 설정 모달의 상태 + 핸들러 관리
import { useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  toTimeSettingDraftRows, createDefaultWeeklyRules, makeTimeSettingRowKey,
  normalizeHHmmInput, normalizeBreakMinutesInput, normalizePolicyMinutesInput,
  calcDraftWeeklyScheduledMinutes, isValidHHmm, WEEKDAY_ORDER,
} from './utils';

/**
 * 출퇴근 시간관리 모달 관련 상태 + 핸들러
 * @param {object} params
 * @param {Function} params.showToast - 토스트 표시 함수
 * @param {string}   params.companyCode - 회사 코드 ('PINE', 'DOT', 'LNJ' 등, 기본 'PINE')
 * @param {string}   params.apiBasePath - API 경로 prefix (외부회사: '/api/admin/ext/DOT' 등)
 */
export function useTimeSettings({ showToast, companyCode = 'PINE', apiBasePath = '/api', settingsPathBuilder }) {
  const [timeSettingsOpen,    setTimeSettingsOpen]    = useState(false);
  const [timeSettingsLoading, setTimeSettingsLoading] = useState(false);
  const [timeSettingsSaving,  setTimeSettingsSaving]  = useState(false);
  const [timeSettingsDraftRows, setTimeSettingsDraftRows] = useState([]);
  const [timeSettingsPolicy,  setTimeSettingsPolicy]  = useState({
    late_grace_minutes: 0,
    early_leave_grace_minutes: 0,
  });

  // 각 템플릿의 주간 소정근로시간 → 연차 발생 여부 미리보기 계산
  const templateWeeklyPreview = useMemo(() => {
    const THRESHOLD = 15 * 60; // 주 15시간(900분) 이상이면 연차 발생
    return timeSettingsDraftRows.map((row) => {
      const weeklyMinutes = calcDraftWeeklyScheduledMinutes(row.weekly_rules);
      return { rowKey: row._rowKey, weeklyMinutes, hasAnnualLeave: weeklyMinutes >= THRESHOLD };
    });
  }, [timeSettingsDraftRows]);

  function getSettingsUrl() {
    const code = companyCode || 'PINE';
    if (typeof settingsPathBuilder === 'function') return settingsPathBuilder(code);
    // 외부회사 모드는 apiBasePath 가 /api/admin/ext/{CODE} → 그 회사 ext 라우트.
    //   평범 모드(apiBasePath='/api')는 뷰어 자기 회사 → 메인 라우트(Fix B 가 자기 회사로 스코프).
    //   'PINE' 하드코딩 대신 apiBasePath 로 판별(테넌트 메인회사도 메인 라우트로 가게).
    return apiBasePath && apiBasePath !== '/api'
      ? `${apiBasePath}/attendance-time-settings`
      : '/api/admin/attendance-time-settings';
  }

  // ── 모달 열기: 서버에서 최신 설정 로드 ──────────────────────────────────────
  async function openTimeSettingsModal() {
    setTimeSettingsOpen(true);
    setTimeSettingsLoading(true);

    // 회사별 출퇴근 시간 설정 API 호출 (외부회사: /api/admin/ext/{CODE}/attendance-time-settings)
    const settingsUrl = getSettingsUrl();
    const result = await apiClient.get(settingsUrl);
    if (!result.ok) {
      showToast(result.data?.message || '출퇴근 시간 설정 조회 실패', 'error');
      setTimeSettingsDraftRows([]);
      setTimeSettingsPolicy({ late_grace_minutes: 0, early_leave_grace_minutes: 0 });
      setTimeSettingsLoading(false);
      return;
    }

    setTimeSettingsDraftRows(toTimeSettingDraftRows(result.data?.data));
    setTimeSettingsPolicy({
      late_grace_minutes: normalizePolicyMinutesInput(result.data?.data?.policy?.late_grace_minutes),
      early_leave_grace_minutes: normalizePolicyMinutesInput(result.data?.data?.policy?.early_leave_grace_minutes),
    });
    setTimeSettingsLoading(false);
  }

  // ── 템플릿 행 추가 ──────────────────────────────────────────────────────────
  function addTimeSettingRow() {
    const baseIndex = timeSettingsDraftRows.length + 1;
    setTimeSettingsDraftRows((prev) => [
      ...prev,
      {
        _rowKey: makeTimeSettingRowKey(),
        id: `template_${Date.now()}`,
        name: `근무유형${baseIndex}`,
        weekly_rules: createDefaultWeeklyRules(),
      },
    ]);
  }

  // ── 템플릿 행 필드 수정 (이름 등) ──────────────────────────────────────────
  function patchTimeSettingRow(rowKey, field, value) {
    setTimeSettingsDraftRows((prev) => prev.map((row) => (
      row._rowKey === rowKey ? { ...row, [field]: value } : row
    )));
  }

  // ── 요일별 규칙 필드 수정 ──────────────────────────────────────────────────
  function patchTimeSettingRule(rowKey, dayKey, field, value) {
    // is_workday: boolean / break_minutes: 숫자(빈값→null) / half_leave_threshold: HH:mm(빈값→null) / 시간: HH:mm
    const safeValue = field === 'is_workday'
      ? Boolean(value)
      : field === 'break_minutes'
        ? (value === '' ? null : Number.isFinite(Number(value)) ? Math.max(0, Math.floor(Number(value))) : null)
        : field === 'half_leave_threshold'
          ? (value === '' ? null : normalizeHHmmInput(value))
          : field === 'work_location'
            ? (String(value || '').trim().slice(0, 100) || null)
            : normalizeHHmmInput(value);

    setTimeSettingsDraftRows((prev) => prev.map((row) => {
      if (row._rowKey !== rowKey) return row;
      const dayRule = row.weekly_rules?.[dayKey] || { is_workday: false, start_time: '', end_time: '' };
      // is_workday 전환 시 기본 시간 자동 입력
      const nextRule = field === 'is_workday'
        ? { ...dayRule, is_workday: safeValue, start_time: safeValue ? (dayRule.start_time || '09:30') : '', end_time: safeValue ? (dayRule.end_time || '18:00') : '' }
        : { ...dayRule, [field]: safeValue };
      return { ...row, weekly_rules: { ...row.weekly_rules, [dayKey]: nextRule } };
    }));
  }

  // ── 템플릿 행 삭제 ──────────────────────────────────────────────────────────
  function removeTimeSettingRow(rowKey) {
    setTimeSettingsDraftRows((prev) => prev.filter((row) => row._rowKey !== rowKey));
  }

  // ── 설정 저장 ──────────────────────────────────────────────────────────────
  async function saveTimeSettings() {
    const payloadRows = timeSettingsDraftRows.map((row, index) => ({
      id: String(row.id || `template_${index + 1}`).trim(),
      name: String(row.name || '').trim(),
      // 계약상 1일 소정근로시간(선택) — 빈 값이면 null(자동 계산)
      prescribed_daily_hours: String(row.prescribed_daily_hours ?? '').trim() === ''
        ? null : Number(row.prescribed_daily_hours),
      weekly_rules: WEEKDAY_ORDER.reduce((acc, dayKey) => {
        const source = row.weekly_rules?.[dayKey] || {};
        const isWorkday = Boolean(source.is_workday);
        const rawThresh = source.half_leave_threshold;
        const halfLeaveThreshold = (isWorkday && rawThresh && isValidHHmm(String(rawThresh).trim()))
          ? String(rawThresh).trim() : null;
        acc[dayKey] = {
          is_workday: isWorkday,
          start_time: isWorkday ? String(source.start_time || '').trim() : '',
          end_time:   isWorkday ? String(source.end_time || '').trim() : '',
          break_minutes: isWorkday ? normalizeBreakMinutesInput(source.break_minutes) : null,
          half_leave_threshold: halfLeaveThreshold,
          work_location: isWorkday ? (String(source.work_location || '').trim() || null) : null,
        };
        return acc;
      }, {}),
    }));

    setTimeSettingsSaving(true);
    // 회사별 출퇴근 시간 설정 저장 API
    const saveUrl = getSettingsUrl();
    const result = await apiClient.put(saveUrl, {
      company_code: companyCode || 'PINE',
      policy: {
        late_grace_minutes: normalizePolicyMinutesInput(timeSettingsPolicy.late_grace_minutes),
        early_leave_grace_minutes: normalizePolicyMinutesInput(timeSettingsPolicy.early_leave_grace_minutes),
      },
      templates: payloadRows,
    });
    setTimeSettingsSaving(false);

    if (!result.ok) { showToast(result.data?.message || '출퇴근 시간 설정 저장 실패', 'error'); return; }

    setTimeSettingsDraftRows(toTimeSettingDraftRows(result.data?.data));
    setTimeSettingsPolicy({
      late_grace_minutes: normalizePolicyMinutesInput(result.data?.data?.policy?.late_grace_minutes),
      early_leave_grace_minutes: normalizePolicyMinutesInput(result.data?.data?.policy?.early_leave_grace_minutes),
    });
    showToast('출퇴근 시간 설정이 저장되었습니다.', 'success');
    setTimeSettingsOpen(false);
  }

  return {
    timeSettingsOpen, setTimeSettingsOpen,
    timeSettingsLoading,
    timeSettingsSaving,
    timeSettingsDraftRows,
    timeSettingsPolicy, setTimeSettingsPolicy,
    templateWeeklyPreview,
    openTimeSettingsModal,
    addTimeSettingRow,
    patchTimeSettingRow,
    patchTimeSettingRule,
    removeTimeSettingRow,
    saveTimeSettings,
  };
}
